import type {
    BjjWebsocketMessage,
    JudoWebsocketMessage,
    KarateKumiteWebsocketMessage,
    RoundBasedWebsocketMessage,
    SubmissionFighting2WebsocketMessage,
    TaekwondoWebsocketMessage,
    WebSocketScoreboardMessage,
    WrestlingWebsocketMessage,
} from "../Scoreboard/Common/types";
import { ROUND_OVERTIME_NO_LIMIT, ROUND_OVERTIME_WITH_LIMIT, ROUND_SECOND_OVERTIME_WITH_LIMIT } from "../Common/consts";

export type LiveFightScore = {
    firstCompetitorPoints?: string;
    secondCompetitorPoints?: string;
    roundStatus?: LiveFightRoundStatus;
};

export type LiveFightRoundStatus =
    | { type: "round"; roundInProgress: number; numberOfRounds: number; breakInProgress: boolean }
    | { type: "overtime"; overtimeType: "limited" | "unlimited" | "secondLimited" }
    | { type: "goldenScore" };

export type LiveFightRoundStatusLabels = {
    breakBetweenRounds: string;
    overtime: string;
    goldenScore: string;
};

export function formatLiveFightRoundStatus(roundStatus: LiveFightRoundStatus, labels: LiveFightRoundStatusLabels): string {
    if (roundStatus.type === "goldenScore") {
        return labels.goldenScore;
    }

    if (roundStatus.type === "overtime") {
        return labels.overtime;
    }

    const roundNumber = `${roundStatus.roundInProgress}/${roundStatus.numberOfRounds}`;
    return roundStatus.breakInProgress ? `☕ ${labels.breakBetweenRounds} → ${roundNumber}` : roundNumber;
}

function formatRoundBasedPoints(majorPoints?: number, penaltyPoints?: number): string {
    if (majorPoints !== undefined && penaltyPoints !== undefined) {
        return `${majorPoints}:${penaltyPoints}`;
    }

    if (majorPoints !== undefined) {
        return `${majorPoints}`;
    }

    if (penaltyPoints !== undefined) {
        return `${penaltyPoints}`;
    }

    return "";
}

function isBjjMessage(message: WebSocketScoreboardMessage): message is BjjWebsocketMessage {
    return typeof message.type === "string" && (message.type.startsWith("bjj") || message.type === "newaza");
}

function isSubmissionFightingMessage(message: WebSocketScoreboardMessage): message is SubmissionFighting2WebsocketMessage {
    return message.type === "submissionFighting2";
}

function isRoundBasedMessage(message: WebSocketScoreboardMessage): message is RoundBasedWebsocketMessage {
    return message.type === "roundBased";
}

function overtimeStatusFromRoundInProgress(roundInProgress: number): LiveFightRoundStatus | undefined {
    if (roundInProgress === ROUND_OVERTIME_WITH_LIMIT) {
        return { type: "overtime", overtimeType: "limited" };
    }
    if (roundInProgress === ROUND_OVERTIME_NO_LIMIT) {
        return { type: "overtime", overtimeType: "unlimited" };
    }
    if (roundInProgress === ROUND_SECOND_OVERTIME_WITH_LIMIT) {
        return { type: "overtime", overtimeType: "secondLimited" };
    }

    return undefined;
}

function normalizeRoundStatus({
    roundInProgress,
    numberOfRounds,
    breakInProgress,
}: {
    roundInProgress: number;
    numberOfRounds: number;
    breakInProgress: boolean;
}): LiveFightRoundStatus | undefined {
    const overtimeStatus = overtimeStatusFromRoundInProgress(roundInProgress);
    if (overtimeStatus) {
        return overtimeStatus;
    }

    if (
        !Number.isInteger(roundInProgress) ||
        !Number.isInteger(numberOfRounds) ||
        numberOfRounds <= 1 ||
        roundInProgress < 1 ||
        roundInProgress > numberOfRounds
    ) {
        return undefined;
    }

    return {
        type: "round",
        roundInProgress,
        numberOfRounds,
        breakInProgress,
    };
}

function isWrestlingMessage(message: WebSocketScoreboardMessage): message is WrestlingWebsocketMessage {
    return message.type === "wrestling";
}

function isJudoMessage(message: WebSocketScoreboardMessage): message is JudoWebsocketMessage {
    return message.type === "judo";
}

function isTaekwondoMessage(message: WebSocketScoreboardMessage): message is TaekwondoWebsocketMessage {
    return message.type === "taekwondo";
}

function isKarateKumiteMessage(message: WebSocketScoreboardMessage): message is KarateKumiteWebsocketMessage {
    return message.type === "karateKumite";
}

export function normalizeLiveFightScore(message: WebSocketScoreboardMessage): LiveFightScore | null {
    if (isBjjMessage(message)) {
        const roundStatus: LiveFightRoundStatus | undefined = message.goldenScore ? { type: "goldenScore" } : undefined;

        if (message.switchedCompetitors) {
            return {
                firstCompetitorPoints: `${message.competitor2MajorPoints}:${message.competitor2Advantages}:${message.competitor2Penalties}`,
                secondCompetitorPoints: `${message.competitor1MajorPoints}:${message.competitor1Advantages}:${message.competitor1Penalties}`,
                ...(roundStatus ? { roundStatus } : {}),
            };
        }

        return {
            firstCompetitorPoints: `${message.competitor1MajorPoints}:${message.competitor1Advantages}:${message.competitor1Penalties}`,
            secondCompetitorPoints: `${message.competitor2MajorPoints}:${message.competitor2Advantages}:${message.competitor2Penalties}`,
            ...(roundStatus ? { roundStatus } : {}),
        };
    }

    if (isSubmissionFightingMessage(message) || isRoundBasedMessage(message)) {
        const roundStatus = isRoundBasedMessage(message)
            ? normalizeRoundStatus(message)
            : overtimeStatusFromRoundInProgress(message.roundInProgress);

        if (
            isRoundBasedMessage(message) &&
            message.redMajorPoints === undefined &&
            message.redPenaltyPoints === undefined &&
            message.blueMajorPoints === undefined &&
            message.bluePenaltyPoints === undefined
        ) {
            return roundStatus ? { roundStatus } : null;
        }

        if (message.switchedCompetitors) {
            return {
                firstCompetitorPoints: formatRoundBasedPoints(message.blueMajorPoints, message.bluePenaltyPoints),
                secondCompetitorPoints: formatRoundBasedPoints(message.redMajorPoints, message.redPenaltyPoints),
                ...(roundStatus ? { roundStatus } : {}),
            };
        }

        return {
            firstCompetitorPoints: formatRoundBasedPoints(message.redMajorPoints, message.redPenaltyPoints),
            secondCompetitorPoints: formatRoundBasedPoints(message.blueMajorPoints, message.bluePenaltyPoints),
            ...(roundStatus ? { roundStatus } : {}),
        };
    }

    if (isWrestlingMessage(message)) {
        const roundStatus = normalizeRoundStatus(message);

        if (message.switchedCompetitors) {
            return {
                firstCompetitorPoints: `${message.major2}:${message.penalties2}`,
                secondCompetitorPoints: `${message.major1}:${message.penalties1}`,
                ...(roundStatus ? { roundStatus } : {}),
            };
        }

        return {
            firstCompetitorPoints: `${message.major1}:${message.penalties1}`,
            secondCompetitorPoints: `${message.major2}:${message.penalties2}`,
            ...(roundStatus ? { roundStatus } : {}),
        };
    }

    if (isJudoMessage(message)) {
        const roundStatus: LiveFightRoundStatus | undefined = message.goldenScore ? { type: "goldenScore" } : undefined;

        if (message.switchedCompetitors) {
            return {
                firstCompetitorPoints: `${message.ippon2}:${message.wazaari2}:${message.yuko2}:${message.shido2}`,
                secondCompetitorPoints: `${message.ippon1}:${message.wazaari1}:${message.yuko1}:${message.shido1}`,
                ...(roundStatus ? { roundStatus } : {}),
            };
        }

        return {
            firstCompetitorPoints: `${message.ippon1}:${message.wazaari1}:${message.yuko1}:${message.shido1}`,
            secondCompetitorPoints: `${message.ippon2}:${message.wazaari2}:${message.yuko2}:${message.shido2}`,
            ...(roundStatus ? { roundStatus } : {}),
        };
    }

    if (isTaekwondoMessage(message)) {
        const roundStatus: LiveFightRoundStatus | undefined = message.goldenRound ? { type: "goldenScore" } : undefined;

        if (message.switchedCompetitors) {
            return {
                firstCompetitorPoints: `${message.competitor2Points}`,
                secondCompetitorPoints: `${message.competitor1Points}`,
                ...(roundStatus ? { roundStatus } : {}),
            };
        }

        return {
            firstCompetitorPoints: `${message.competitor1Points}`,
            secondCompetitorPoints: `${message.competitor2Points}`,
            ...(roundStatus ? { roundStatus } : {}),
        };
    }

    if (isKarateKumiteMessage(message)) {
        const roundStatus = overtimeStatusFromRoundInProgress(message.roundInProgress);

        if (message.switchedCompetitors) {
            return {
                firstCompetitorPoints: `${message.competitor2Wazari}:${message.competitor2Chui}:${message.competitor2Genten}`,
                secondCompetitorPoints: `${message.competitor1Wazari}:${message.competitor1Chui}:${message.competitor1Genten}`,
                ...(roundStatus ? { roundStatus } : {}),
            };
        }

        return {
            firstCompetitorPoints: `${message.competitor1Wazari}:${message.competitor1Chui}:${message.competitor1Genten}`,
            secondCompetitorPoints: `${message.competitor2Wazari}:${message.competitor2Chui}:${message.competitor2Genten}`,
            ...(roundStatus ? { roundStatus } : {}),
        };
    }

    return null;
}
